import { DefaultQueryParams } from '@frontend/api-utils';
import { ObjectSelectInput, ObjectSelectProps } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { SpotClient } from '@frontend/spot/api';
import { Spot, SpotsQueryParams } from '@frontend/spot/types';
import React, { useMemo, useState } from 'react';

const ID = 'spot-select';
export interface SpotSelectV2Props extends ObjectSelectProps<Spot, SpotsQueryParams> {
    excludeSpotIds?: string[];
}

export const SpotSelectV2 = (props: SpotSelectV2Props) => {
    const { excludeSpotIds, ...selectProps } = props;
    const [v, cV] = useState<Spot | null>(null);
    const excludedSpotIds = useMemo(() => new Set(excludeSpotIds), [excludeSpotIds]);

    // Keep internal object in sync when the controlled id is cleared from outside.
    React.useEffect(() => {
        if (selectProps.value == null) {
            cV(null);
        }
    }, [selectProps.value]);

    return (
        <ObjectSelectInput<Spot, DefaultQueryParams | SpotsQueryParams>
            {...selectProps}
            id={`${ID}${selectProps.id && '-' + selectProps.id}`}
            label={selectProps.label || CommonMessage.OBJECTS.SPOT.DEFAULT}
            className={selectProps.className}
            onChange={(selected) => {
                const nextId = selected?.id ?? null;
                if (nextId !== (selectProps.value ?? null)) {
                    selectProps.onChange?.(nextId);
                }
                selectProps.onChangeObject?.(selected);
                cV(selected);
            }}
            required={selectProps.required}
            queryParams={selectProps.queryParams}
            value={v}
            isClearable={selectProps.isClearable}
            idValue={selectProps.value !== undefined ? selectProps.value : v?.id}
            singleFetch={(id) =>
                SpotClient.resolveSpots([id]).then((res) => {
                    return res.results[0];
                })
            }
            fetch={(arg) =>
                SpotClient.fetchSpots({ ...arg, sort_field: 'name', sort_direction: 'asc' }).then((response) => ({
                    ...response,
                    results: response.results.filter((spot) => !excludedSpotIds.has(spot.id))
                }))
            }
            itemLabel={(spot) => spot.name}
            disableFetch={selectProps.disableFetch}
            disabled={selectProps.disabled}
            useQueryParam={
                selectProps.allowURLChange
                    ? {
                          param: selectProps.queryKeyOverride ?? selectProps.id ?? 'spot_id',
                          fetch: (id) =>
                              SpotClient.resolveSpots([id]).then((res) => {
                                  return res.results[0];
                              })
                      }
                    : undefined
            }
        />
    );
};
